import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import api from '../../utils/api';
import ProductCard from '../../components/common/ProductCard';
import { cldImg } from '../../utils/cloudinary';

function ProductSkeleton() {
  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 animate-pulse">
      <div className="aspect-square bg-gray-100" />
      <div className="p-2 space-y-1.5">
        <div className="h-2.5 bg-gray-100 rounded w-3/4" />
        <div className="h-3.5 bg-gray-100 rounded w-1/2" />
      </div>
    </div>
  );
}

function CategorySection({ category, headerHeight }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/products', {
      params: { category: category.id, limit: 6, sort: 'createdAt', order: 'DESC' },
    })
      .then(r => setProducts(r.data.products || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category.id]);

  return (
    /* scrollMarginTop offsets for the sticky header so scrollIntoView lands correctly */
    <div id={`cat-${category.id}`} style={{ scrollMarginTop: `${headerHeight}px` }}>
      <div className="flex items-center justify-between px-3 py-2.5 md:px-1 md:py-4 bg-white border-t border-b border-gray-100 md:bg-transparent md:border-0">
        <div className="flex items-center gap-2 md:gap-3">
          {category.image && (
            <div className="w-7 h-7 md:w-10 md:h-10 rounded-lg overflow-hidden border border-gray-100 flex-shrink-0">
              <img src={cldImg(category.image, 400)} alt={category.name} className="w-full h-full object-cover" />
            </div>
          )}
          <h2 className="text-sm md:text-lg font-bold text-brand-dark">{category.name}</h2>
        </div>
        <Link
          to={`/category/${category.slug}`}
          className="flex items-center gap-0.5 text-[11px] md:text-sm font-semibold text-brand-yellow whitespace-nowrap hover:text-brand-yellow-dark transition-colors"
        >
          See All <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
        </Link>
      </div>

      <div className="p-2.5 md:p-0 md:pb-4 bg-gray-50 md:bg-transparent">
        {loading ? (
          <div className="grid grid-cols-2 gap-2.5 md:flex md:gap-4 md:overflow-x-auto scrollbar-hide md:pb-2">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="md:flex-shrink-0 md:w-52"><ProductSkeleton /></div>)}
          </div>
        ) : products.length === 0 ? (
          <div className="py-8 flex flex-col items-center text-center">
            <p className="text-3xl mb-2">🛍️</p>
            <p className="text-xs text-gray-400">No products yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2.5 md:flex md:gap-4 md:overflow-x-auto scrollbar-hide md:pb-2">
            {products.map(p => <div key={p.id} className="md:flex-shrink-0 md:w-52"><ProductCard product={p} /></div>)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CategoriesPage() {
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [activeId, setActiveId]         = useState(null);
  const [headerHeight, setHeaderHeight] = useState(56);
  const [sidebarHeight, setSidebarHeight] = useState('calc(100dvh - 120px)');

  const sidebarRef    = useRef(null);
  const categoriesRef = useRef([]);
  const isScrolling   = useRef(false);
  const headerHRef    = useRef(56);

  /* Kill footer mt-16 + colour main pb-16 */
  useEffect(() => {
    const main   = document.querySelector('main');
    const footer = document.querySelector('footer');
    if (main)   main.classList.add('bg-gray-50');
    if (footer) footer.style.marginTop = '0';
    return () => {
      if (main)   main.classList.remove('bg-gray-50');
      if (footer) footer.style.marginTop = '';
    };
  }, []);

  /* Measure header height */
  useLayoutEffect(() => {
    const h = document.querySelector('header')?.offsetHeight || 56;
    headerHRef.current = h;
    setHeaderHeight(h);
    setSidebarHeight(`calc(100dvh - ${h + 64}px)`);
  }, []);

  /* Fetch categories */
  useEffect(() => {
    api.get('/categories')
      .then(r => {
        const cats = r.data.categories || [];
        categoriesRef.current = cats;
        setCategories(cats);
        if (cats.length > 0) setActiveId(cats[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* Scroll spy */
  useEffect(() => {
    const onScroll = () => {
      if (isScrolling.current) return;
      /* A section is active when its header has reached the vertical level of its
         OWN sidebar item — i.e. the section lines up beside its sidebar thumbnail. */
      const cats = categoriesRef.current;
      const sb   = sidebarRef.current;
      let current = cats[0]?.id ?? null;
      for (const cat of cats) {
        const sec = document.getElementById(`cat-${cat.id}`);
        const tab = sb?.querySelector(`[data-id="${cat.id}"]`);
        if (!sec || !tab) continue;
        const tabRect = tab.getBoundingClientRect();
        const tabMid  = tabRect.top + tabRect.height / 2;
        if (sec.getBoundingClientRect().top <= tabMid) {
          current = cat.id;
        }
      }
      setActiveId(prev => (prev === current ? prev : current));
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Keep active sidebar tab visible — scroll ONLY the sidebar, never the window
     (scrollIntoView would bubble to the page and fight the section scroll) */
  useEffect(() => {
    if (!activeId || !sidebarRef.current) return;
    const sb  = sidebarRef.current;
    const btn = sb.querySelector(`[data-id="${activeId}"]`);
    if (!btn) return;
    const top    = btn.offsetTop;
    const bottom = top + btn.offsetHeight;
    if (top < sb.scrollTop) {
      sb.scrollTo({ top: top - 8, behavior: 'smooth' });
    } else if (bottom > sb.scrollTop + sb.clientHeight) {
      sb.scrollTo({ top: bottom - sb.clientHeight + 8, behavior: 'smooth' });
    }
  }, [activeId]);

  /* Click sidebar → scroll the page to that section */
  const goToSection = (catId) => {
    const el = document.getElementById(`cat-${catId}`);
    if (!el) return;

    setActiveId(catId);
    isScrolling.current = true;

    /* Compute absolute target, offset by the sticky header height.
       window.scrollTo is far more reliable on iOS Safari than scrollIntoView. */
    const top = el.getBoundingClientRect().top + window.scrollY - headerHRef.current;
    window.scrollTo({ top, behavior: 'smooth' });

    clearTimeout(goToSection._t);
    goToSection._t = setTimeout(() => { isScrolling.current = false; }, 700);
  };

  return (
    <div className="flex bg-gray-50 md:max-w-7xl md:mx-auto md:px-4">

      {/* ── Sidebar ── */}
      <div
        ref={sidebarRef}
        className="flex-shrink-0 w-[78px] md:hidden bg-white border-r border-gray-100 sticky self-start overflow-y-auto"
        style={{ top: headerHeight, maxHeight: sidebarHeight }}
      >
        {loading
          ? [1,2,3,4,5,6,7].map(i => (
              <div key={i} className="flex flex-col items-center gap-1.5 py-3 px-1.5 md:flex-row md:gap-3 md:px-4 md:py-2.5 animate-pulse">
                <div className="w-12 h-12 md:w-11 md:h-11 rounded-xl bg-gray-100 flex-shrink-0" />
                <div className="h-2.5 w-10 md:w-24 bg-gray-100 rounded" />
              </div>
            ))
          : categories.map(cat => {
              const active = activeId === cat.id;
              return (
                <button
                  key={cat.id}
                  data-id={cat.id}
                  onClick={() => goToSection(cat.id)}
                  className={`w-full flex flex-col items-center gap-1.5 py-3 px-1.5 md:flex-row md:items-center md:gap-3 md:px-4 md:py-2.5 relative transition-colors md:hover:bg-[#FFF8E1]
                    ${active ? 'bg-[#FFF8E1]' : 'bg-white'}`}
                >
                  {active && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-brand-yellow rounded-r-full" />
                  )}
                  <div className={`w-12 h-12 md:w-11 md:h-11 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0
                    ${active ? 'ring-2 ring-brand-yellow ring-offset-1' : 'border border-gray-100 bg-gray-50'}`}>
                    {cat.image
                      ? <img src={cldImg(cat.image, 400)} alt={cat.name} className="w-full h-full object-cover" />
                      : <span className="text-lg font-bold text-brand-yellow">{cat.name?.[0]}</span>
                    }
                  </div>
                  <span className={`text-[10px] leading-tight text-center line-clamp-2 md:text-sm md:text-left md:leading-snug md:flex-1
                    ${active ? 'text-brand-dark font-bold' : 'text-gray-500 font-medium'}`}>
                    {cat.name}
                  </span>
                </button>
              );
            })
        }
      </div>

      {/* ── Right: all sections ── */}
      <div className="flex-1 min-w-0">
        {loading
          ? [1,2,3].map(i => (
              <div key={i}>
                <div className="h-11 md:h-12 bg-white border-t border-b border-gray-100 md:bg-transparent md:border-0" />
                <div className="p-2.5 md:p-0 md:pb-4 bg-gray-50 md:bg-transparent grid grid-cols-2 gap-2.5 md:flex md:gap-4 md:overflow-x-auto scrollbar-hide">
                  {[1,2,3,4,5,6].map(j => <div key={j} className="md:flex-shrink-0 md:w-52"><ProductSkeleton /></div>)}
                </div>
              </div>
            ))
          : categories.map(cat => (
              <CategorySection key={cat.id} category={cat} headerHeight={headerHeight} />
            ))
        }
      </div>
    </div>
  );
}
